import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import { fetchPrivacyPolicy } from '../services/airtableService';
import './PrivacyPolicy.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const PrivacyPolicy = () => {
  const [privacyData, setPrivacyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stylesLoaded, setStylesLoaded] = useState(false);

  useEffect(() => {
    // CSS 로딩 완료 확인
    const checkStylesLoaded = () => {
      const testElement = document.createElement('div');
      testElement.className = 'privacy-policy-container';
      testElement.style.display = 'none';
      document.body.appendChild(testElement);
      
      const computedStyle = window.getComputedStyle(testElement);
      const isLoaded = computedStyle.writingMode === 'horizontal-tb';
      
      document.body.removeChild(testElement);
      return isLoaded;
    };

    // 스타일 로딩 확인
    const checkStyles = () => {
      if (checkStylesLoaded()) {
        setStylesLoaded(true);
        // DOM에 클래스 추가하여 CSS 트랜지션 활성화
        setTimeout(() => {
          const container = document.querySelector('.privacy-policy-container');
          if (container) {
            container.classList.add('styles-loaded');
          }
        }, 100);
      } else {
        setTimeout(checkStyles, 50);
      }
    };

    // 즉시 확인 후 주기적으로 체크
    checkStyles();

    const loadPrivacyPolicy = async () => {
      try {
        setLoading(true);
        const data = await fetchPrivacyPolicy();
        setPrivacyData(data);
      } catch (err) {
        console.error('Privacy Policy 로드 실패:', err);
        setError('개인정보 처리방침을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadPrivacyPolicy();
  }, []);

  const containerClassName = `privacy-policy-container${stylesLoaded ? ' styles-loaded' : ''}`;

  // 스타일이 로드되지 않았거나 데이터 로딩 중일 때는 빈 화면 표시
  if (loading || !stylesLoaded) {
    return (
      <div className={containerClassName} style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ 
          textAlign: 'center',
          color: '#666',
          fontSize: '1.1rem'
        }}>
          {loading ? '로딩 중...' : '스타일 로딩 중...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClassName}>
        <div className="privacy-policy-content">
          <div className="error">
            <h1 style={{
              display: 'block',
              writingMode: 'horizontal-tb',
              textOrientation: 'mixed',
              direction: 'ltr',
              whiteSpace: 'normal',
              wordBreak: 'keep-all',
              flexDirection: 'unset',
              flexWrap: 'unset'
            }}>개인정보 처리방침</h1>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>다시 시도</button>
          </div>
        </div>
      </div>
    );
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '개인정보 처리방침 - Flowto.AI',
    description: 'Flowto.AI 개인정보 처리방침',
    url: 'https://flowto-ai.com/privacy-policy'
  };

  return (
    <div className={containerClassName}>
      <SEO
        title="개인정보 처리방침"
        description="Flowto.AI 개인정보 처리방침"
        url="/privacy-policy"
        type="website"
        structuredData={structuredData}
      />
      <div className="privacy-policy-content">
        <header className="privacy-policy-header" style={{
          writingMode: 'horizontal-tb',
          textOrientation: 'mixed',
          direction: 'ltr'
        }}>
          <h1 style={{
            display: 'block',
            writingMode: 'horizontal-tb',
            textOrientation: 'mixed',
            direction: 'ltr',
            whiteSpace: 'normal',
            wordBreak: 'keep-all',
            flexDirection: 'unset',
            flexWrap: 'unset'
          }}>개인정보 처리방침</h1>
          {privacyData?.lastModified && (
            <p className="last-updated">
              최종 수정일: {new Date(privacyData.lastModified).toLocaleDateString('ko-KR')}
            </p>
          )}
        </header>
        
        <div className="privacy-policy-body">
          {privacyData ? (
            <div className="privacy-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{privacyData}</ReactMarkdown>
            </div>
          ) : (
            <p>개인정보 처리방침 내용이 없습니다.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
